<template>
  <div class="wrapper-form">
    <!-- ✅ 클릭 가능한 div (input 대체) -->
    <div class="dropdown-trigger" ref="refTitle" @click="toggleDropdown">
      <p>
        {{ titleDisplayed }}
      </p>
      <component :is="isVisibleDropdownTitle ? IconChevronUp : IconChevronDown" class="icon-medium" />
    </div>

    <!-- ✅ 드롭다운 -->
    <Dropdown v-if="isVisibleDropdownTitle" :trigger-ref="refTitle" position="below" @close="closeDropdown">
      <ul class="wrapper-list" :style="{ width: `${inputWidth}px` }">
        <li
          v-for="titleDefault in listTitleDefault"
          :key="titleDefault.value"
          @click="onClickTitleDefault(titleDefault)"
        >
          <p>{{ titleDefault.label }}</p>
        </li>
      </ul>
    </Dropdown>

    <textarea rows="10" v-model="formInput.content" />
    <div class="ct-btn-wrap">
      <ButtonProcess :disabled="isDisabledSubmit" @click="onClickSubmit">
        {{ t('self_service_contact_submit') }}
      </ButtonProcess>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { addSelfServiceInquiry } from '@/api/apiService';
import IconChevronDown from '@/components/icons/IconChevronDown.vue';
import IconChevronUp from '@/components/icons/IconChevronUp.vue';
import ButtonProcess from '@/components/shared/base/ButtonProcess.vue';
import Dropdown from '@/components/shared/base/Dropdown.vue';
import { useToastStore } from '@/stores/toast';

const toastStore = useToastStore();

const { t, locale } = useI18n();

const emit = defineEmits<{
  'inquiry-submit': [];
}>();

const refTitle = ref<HTMLElement>();
const isVisibleDropdownTitle = ref(false);

const formInput = ref({
  title: '',
  content: '',
});

// 문의 유형 목록
const listTitleDefault = computed(() => {
  return [
    { label: t('inquiry_title_service'), value: 'inquiry_title_service' },
    { label: t('inquiry_title_account'), value: 'inquiry_title_account' },
    { label: t('inquiry_title_problem'), value: 'inquiry_title_problem' },
    { label: t('inquiry_title_proposal'), value: 'inquiry_title_proposal' },
    {
      label: t('inquiry_title_partnership'),
      value: 'inquiry_title_partnership',
    },
    { label: t('inquiry_title_etc'), value: 'inquiry_title_etc' },
  ];
});

// 제출 버튼 비활성화 여부
const isDisabledSubmit = computed(() => Object.values(formInput.value).some((value) => !value));

// 화면에 표시될 문의 유형 문구
const titleDisplayed = computed(() => {
  const key = formInput.value.title || 'select_inquiry_category';
  return t(key);
});

// 드롭다운 토글 및 닫기 함수
const toggleDropdown = () => (isVisibleDropdownTitle.value = !isVisibleDropdownTitle.value);

// 드롭다운 닫기 함수
const closeDropdown = () => (isVisibleDropdownTitle.value = false);

const onClickTitleDefault = (newVal) => {
  formInput.value.title = newVal.value;
  closeDropdown();
};

const onClickSubmit = async () => {
  const { success } = await addSelfServiceInquiry(formInput.value);
  if (!success) return;
  formInput.value = { title: '', content: '' };
  emit('inquiry-submit');
  toastStore.info(t('ss_inquiry_post_success'));
};

const inputWidth = ref(0);
onMounted(() => {
  nextTick(() => {
    if (refTitle.value) {
      inputWidth.value = refTitle.value.offsetWidth - 18;
    }
  });
});
window.addEventListener('resize', () => {
  if (refTitle.value) {
    inputWidth.value = refTitle.value.offsetWidth - 18;
  }
});
</script>

<style scoped lang="scss">
.wrapper-form {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  .ct-btn-wrap {
    display: flex;
    justify-content: flex-end;
    .primary-btn {
      width: auto;
      background: $color-gray-900;
    }
  }
}

.dropdown-trigger {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  background: var(--surface-default);
  border: 1px solid var(--border-default);
  cursor: pointer;
  transition: border-color 0.2s;

  &:hover {
    border-color: var(--border-dark);
  }
}

.selected-text {
  color: var(--color-gray-700);
  font-size: 0.9375rem;
}

.icon-chevron {
  width: 1.25rem;
  height: 1.25rem;
  color: var(--color-gray-500);
}

.wrapper-list {
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  padding: 0;
  margin: 0;
  list-style: none;

  li {
    padding: 0.5rem 0.75rem;
    border-radius: 0.5rem;
    cursor: pointer;
    transition: background 0.2s;
    word-break: break-word;

    &:hover {
      background: var(--surface-hover);
    }

    p {
      margin: 0;
      font-size: 0.9375rem;
      color: var(--color-gray-800);
    }
  }
}
</style>
